import React from "react";
import { IMAGES } from "@/lib/config";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Compass } from "lucide-react";

const SERVICES = [
  {
    key: "flooring",
    title: "Flooring Installation",
    body: "Laminate, luxury vinyl plank, engineered hardwood, solid hardwood, and tile flooring — installed level, tight, and lasting.",
    bullets: ["Laminate & LVP", "Hardwood (solid & engineered)", "Porcelain & ceramic tile"],
    image: IMAGES.living,
  },
  {
    key: "tile",
    title: "Tile Installation",
    body: "Bathrooms, kitchens, backsplashes, and full shower systems. Waterproofing, layout, and mosaics executed with precision.",
    bullets: ["Bathrooms & showers", "Kitchen backsplashes", "Floor tile & mosaics"],
    image: IMAGES.bathroom,
  },
  {
    key: "repairs",
    title: "Repairs &amp; Replacements",
    body: "Cracked tiles, damaged planks, squeaky sub-floors, worn grout — we restore surfaces without a full teardown when possible.",
    bullets: ["Tile & plank replacement", "Grout & caulk restoration", "Sub-floor repair"],
    image: IMAGES.repair,
  },
  {
    key: "custom",
    title: "Custom Design Work",
    body: "Herringbone, chevron, medallions, mixed-material transitions, and custom mosaics designed with you.",
    bullets: ["Herringbone & chevron", "Medallions & inlays", "Mixed-material transitions"],
    image: IMAGES.custom,
  },
];

export default function Services() {
  return (
    <div data-testid="services-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="max-w-3xl">
        <div className="eyebrow text-[color:var(--brand-wood)]">Services</div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          Flooring &amp; tile — done right, the first time.
        </h1>
        <p className="mt-5 text-[color:var(--brand-woodDark)]">
          Whether it&apos;s a single backsplash or every floor in the house, we handle the details other crews skip.
        </p>
      </div>

      {/* Guarantee + Process callout */}
      <div className="mt-10 grid gap-4 md:grid-cols-2" data-testid="services-callouts">
        <Link to="/warranty" data-testid="services-warranty-card"
          className="group rounded-2xl bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] p-6 flex items-center gap-5 hover-lift">
          <div className="h-14 w-14 rounded-full bg-white/10 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-7 w-7"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="eyebrow text-white/60">Our guarantee</div>
            <div className="mt-1 font-display text-xl leading-tight">1-year labor warranty on every install</div>
            <div className="mt-1 text-xs text-white/60">If our workmanship fails within 12 months, we fix it — free.</div>
          </div>
          <ArrowRight className="h-5 w-5 opacity-70 group-hover:translate-x-0.5 transition-transform"/>
        </Link>
        <Link to="/what-to-expect" data-testid="services-process-card"
          className="group rounded-2xl border border-black/10 bg-white p-6 flex items-center gap-5 hover-lift">
          <div className="h-14 w-14 rounded-full bg-[color:var(--brand-stone)] text-[color:var(--brand-woodDark)] flex items-center justify-center shrink-0">
            <Compass className="h-7 w-7"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="eyebrow text-[color:var(--brand-wood)]">What to expect</div>
            <div className="mt-1 font-display text-xl leading-tight">Our 6-step process, in plain English</div>
            <div className="mt-1 text-xs text-[color:var(--brand-woodDark)]">No pressure, no surprise upsells. See how a project really goes.</div>
          </div>
          <ArrowRight className="h-5 w-5 text-[color:var(--brand-woodDark)] group-hover:translate-x-0.5 transition-transform"/>
        </Link>
      </div>

      <div className="mt-14 space-y-16">
        {SERVICES.map((s, i) => (
          <div
            key={s.key}
            data-testid={`service-${s.key}`}
            className="grid md:grid-cols-12 gap-10 items-center"
          >
            <div className={`md:col-span-6 ${i % 2 === 1 ? "md:order-2" : ""}`}>
              <div className="rounded-3xl overflow-hidden aspect-[4/3]">
                <img src={s.image} alt={s.title.replace(/&amp;/g, "&")} className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="md:col-span-6">
              <div className="eyebrow text-[color:var(--brand-wood)]">0{i + 1}</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl leading-tight" dangerouslySetInnerHTML={{ __html: s.title }} />
              <p className="mt-4 text-[color:var(--brand-woodDark)]" dangerouslySetInnerHTML={{ __html: s.body }} />
              <ul className="mt-6 flex flex-wrap gap-2">
                {s.bullets.map((b) => (
                  <li key={b} className="rounded-full border border-black/10 bg-white px-3 py-1 text-xs text-[color:var(--brand-woodDark)]">
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex gap-3">
                <Link
                  data-testid={`service-quote-${s.key}`}
                  to="/quote"
                  className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] px-5 py-2.5 text-sm hover:-translate-y-0.5 transition-transform"
                >
                  Get a Quote <ArrowRight className="h-4 w-4"/>
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
